import { useCallback, useEffect, useState, type FormEvent } from "react";
import { api } from "../api";
import type { DnsLookupResult, DnsRecord, DnsZone, PanelUser } from "../types";

const RECORD_TYPES = [
  "A",
  "AAAA",
  "CNAME",
  "MX",
  "TXT",
  "SRV",
  "CAA",
  "NS"
] as const;

export function DnsPage({
  accounts,
  currentUser,
  onRefreshAccounts
}: {
  accounts: PanelUser[];
  currentUser: PanelUser;
  onRefreshAccounts: () => Promise<void>;
}) {
  const staff =
    currentUser.role === "superadmin" || currentUser.role === "admin";
  const reseller = currentUser.role === "reseller";
  const operator = staff || reseller;
  const provisioned = accounts.filter(
    (a) => a.role === "user" && a.provisionStatus === "ready"
  );
  // W6: staff can view all zones; reseller/user scoped to account
  const [userId, setUserId] = useState(
    staff
      ? ""
      : reseller
        ? provisioned[0]?.id || ""
        : currentUser.provisionStatus === "ready"
          ? currentUser.id
          : ""
  );
  const [zones, setZones] = useState<DnsZone[]>([]);
  const [selectedId, setSelectedId] = useState("");
  const [records, setRecords] = useState<DnsRecord[]>([]);
  const [selectedZone, setSelectedZone] = useState<DnsZone | null>(null);
  const [status, setStatus] = useState<Record<string, unknown> | null>(null);
  const [templates, setTemplates] = useState<
    { id: string; name: string; description: string }[]
  >([]);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");

  // create zone form
  const [zoneName, setZoneName] = useState("");
  const [provider, setProvider] = useState<"local" | "external" | "cloudflare">(
    "local"
  );

  // record form
  const [recName, setRecName] = useState("@");
  const [recType, setRecType] = useState<string>("A");
  const [recContent, setRecContent] = useState("");
  const [recTtl, setRecTtl] = useState(3600);
  const [recPriority, setRecPriority] = useState(10);
  const [editingId, setEditingId] = useState<string | null>(null);

  // dig tool
  const [lookupName, setLookupName] = useState("");
  const [lookupType, setLookupType] = useState("A");
  const [lookupServer, setLookupServer] = useState("");
  const [lookupResult, setLookupResult] = useState<DnsLookupResult | null>(
    null
  );

  const loadZones = useCallback(async () => {
    if (!userId && !staff) return;
    setError("");
    try {
      const path =
        userId
          ? `/api/v1/dns/zones?userId=${encodeURIComponent(userId)}`
          : "/api/v1/dns/zones";
      const z = await api<{ items: DnsZone[] }>(path);
      setZones(z.items || []);
      const s = await api<{ status: Record<string, unknown> }>(
        "/api/v1/dns/status"
      );
      setStatus(s.status || null);
      const t = await api<{ items: { id: string; name: string; description: string }[] }>(
        "/api/v1/dns/templates"
      );
      setTemplates(t.items || []);
      if (z.items?.[0] && !selectedId) {
        setSelectedId(z.items[0].id);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [userId, selectedId]);

  const loadZoneDetail = useCallback(async (id: string) => {
    if (!id) {
      setSelectedZone(null);
      setRecords([]);
      return;
    }
    try {
      const res = await api<{ zone: DnsZone; records: DnsRecord[] }>(
        `/api/v1/dns/zones/${id}`
      );
      setSelectedZone(res.zone);
      setRecords(res.records || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  useEffect(() => {
    void loadZones();
  }, [loadZones]);

  useEffect(() => {
    if (selectedId) void loadZoneDetail(selectedId);
  }, [selectedId, loadZoneDetail]);

  useEffect(() => {
    if (!staff && !userId && provisioned[0]?.id) setUserId(provisioned[0].id);
  }, [provisioned, userId, staff]);

  const createZone = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setMsg("");
    if (!userId) {
      setError("Select an account to own the zone");
      return;
    }
    try {
      const res = await api<{ zone: DnsZone }>("/api/v1/dns/zones", {
        method: "POST",
        body: JSON.stringify({
          userId,
          name: zoneName,
          provider
        })
      });
      setMsg(`Zone ${res.zone.name} created (${res.zone.provider})`);
      setZoneName("");
      await loadZones();
      setSelectedId(res.zone.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const deleteZone = async (id: string, name: string) => {
    if (!confirm(`Delete DNS zone ${name}?`)) return;
    setError("");
    try {
      await api(`/api/v1/dns/zones/${id}`, { method: "DELETE" });
      setMsg(`Zone ${name} deleted`);
      if (selectedId === id) setSelectedId("");
      await loadZones();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const saveRecord = async (e: FormEvent) => {
    e.preventDefault();
    if (!selectedId) return;
    setError("");
    setMsg("");
    try {
      if (editingId) {
        await api(`/api/v1/dns/zones/${selectedId}/records/${editingId}`, {
          method: "PATCH",
          body: JSON.stringify({
            name: recName,
            type: recType,
            content: recContent,
            ttl: recTtl,
            priority: recType === "MX" || recType === "SRV" ? recPriority : null
          })
        });
        setMsg("Record updated");
      } else {
        await api(`/api/v1/dns/zones/${selectedId}/records`, {
          method: "POST",
          body: JSON.stringify({
            name: recName,
            type: recType,
            content: recContent,
            ttl: recTtl,
            priority: recType === "MX" || recType === "SRV" ? recPriority : null
          })
        });
        setMsg("Record added");
      }
      setEditingId(null);
      setRecContent("");
      setRecName("@");
      await loadZoneDetail(selectedId);
      await loadZones();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const startEdit = (r: DnsRecord) => {
    setEditingId(r.id);
    setRecName(r.name);
    setRecType(r.type);
    setRecContent(r.content);
    setRecTtl(r.ttl);
    setRecPriority(r.priority ?? 10);
  };

  const deleteRecord = async (id: string) => {
    if (!selectedId || !confirm("Delete this record?")) return;
    try {
      await api(`/api/v1/dns/zones/${selectedId}/records/${id}`, {
        method: "DELETE"
      });
      setMsg("Record deleted");
      await loadZoneDetail(selectedId);
      await loadZones();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const applyTemplate = async (templateId: string) => {
    if (!selectedId) return;
    setError("");
    try {
      const res = await api<{ added: DnsRecord[] }>(
        `/api/v1/dns/zones/${selectedId}/templates`,
        {
          method: "POST",
          body: JSON.stringify({ template: templateId })
        }
      );
      setMsg(`Template applied — ${res.added?.length || 0} record(s) added`);
      await loadZoneDetail(selectedId);
      await loadZones();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const setZoneProvider = async (p: "local" | "external" | "cloudflare") => {
    if (!selectedId) return;
    try {
      await api(`/api/v1/dns/zones/${selectedId}`, {
        method: "PATCH",
        body: JSON.stringify({ provider: p })
      });
      setMsg(`Provider set to ${p}`);
      await loadZoneDetail(selectedId);
      await loadZones();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const exportZone = async () => {
    if (!selectedId) return;
    try {
      const res = await api<{ name: string; content: string }>(
        `/api/v1/dns/zones/${selectedId}/export`
      );
      const blob = new Blob([res.content || ""], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${res.name}.zone`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const runLookup = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    try {
      const res = await api<{ result: DnsLookupResult }>("/api/v1/dns/lookup", {
        method: "POST",
        body: JSON.stringify({
          name: lookupName,
          type: lookupType,
          server: lookupServer || undefined
        })
      });
      setLookupResult(res.result);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  if (!userId && !staff) {
    return (
      <>
        <div className="topbar">
          <div>
            <h1>DNS Zone Manager</h1>
            <p>Your account must be ready before DNS zones are available.</p>
          </div>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => void onRefreshAccounts()}
          >
            Refresh
          </button>
        </div>
      </>
    );
  }

  const localEditable = selectedZone?.provider === "local";

  return (
    <>
      <div className="topbar">
        <div>
          <h1>DNS Zone Manager</h1>
          <p>
            {staff
              ? "WHM · all zones on this server · edit records · dig lookup"
              : "Zone editor · A / MX / TXT / CNAME and more"}
          </p>
        </div>
        <div className="topbar-actions">
          {operator ? (
            <select
              className="inline-select"
              value={userId}
              onChange={(e) => {
                setUserId(e.target.value);
                setSelectedId("");
              }}
              aria-label="Account filter"
            >
              {staff ? <option value="">All accounts</option> : null}
              {provisioned.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.username}
                  {a.primaryDomain ? ` · ${a.primaryDomain}` : ""}
                </option>
              ))}
            </select>
          ) : null}
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => void loadZones()}
          >
            Refresh
          </button>
        </div>
      </div>

      {msg ? <div className="success-banner">{msg}</div> : null}
      {error ? <div className="error-banner">{error}</div> : null}

      {/* W6 hub strip */}
      <div className="whm-stats" style={{ marginBottom: 14 }}>
        <div className="whm-stat">
          <span className="whm-stat-label">Zones</span>
          <span className="whm-stat-value">{zones.length}</span>
          <span className="whm-stat-meta">
            {userId
              ? "Selected account"
              : staff
                ? "Entire server"
                : "Your zones"}
          </span>
        </div>
        <div className="whm-stat">
          <span className="whm-stat-label">Backend</span>
          <span className="whm-stat-value" style={{ fontSize: 16 }}>
            {status ? String(status.backend || "—") : "—"}
          </span>
          <span className="whm-stat-meta mono">
            {status ? String(status.serverIp || "") : ""}
          </span>
        </div>
        <div className="whm-stat">
          <span className="whm-stat-label">Tools</span>
          <div className="row-actions" style={{ marginTop: 6 }}>
            <span className="chip">Zone Editor</span>
            <span className="chip">dig Lookup</span>
            <span className="chip">Export</span>
          </div>
        </div>
      </div>

      {status ? (
        <div className="card full" style={{ marginBottom: 16 }}>
          <div className="card-label">DNS engine</div>
          <p className="card-meta">
            <span className="mono">{String(status.backend)}</span>
            {" · "}
            server IP <span className="mono">{String(status.serverIp)}</span>
            {" · "}
            dig {status.digAvailable ? "available" : "fallback"}
          </p>
          <p className="card-meta faint">{String(status.note || "")}</p>
        </div>
      ) : null}

      <div className="fm-layout">
        <div className="fm-sidebar" style={{ minWidth: 260 }}>
          <form className="panel-form" onSubmit={createZone}>
            <div className="panel-form-title">Create zone</div>
            <div className="field">
              <label>Domain / zone</label>
              <input
                value={zoneName}
                onChange={(e) => setZoneName(e.target.value)}
                placeholder="example.com"
                required
              />
            </div>
            <div className="field">
              <label>Provider</label>
              <select
                value={provider}
                onChange={(e) =>
                  setProvider(e.target.value as typeof provider)
                }
              >
                <option value="local">Local (panel-managed)</option>
                <option value="external">External (registrar / other)</option>
                <option value="cloudflare">Cloudflare (metadata)</option>
              </select>
            </div>
            <button className="btn btn-primary btn-inline" type="submit">
              Create zone
            </button>
          </form>

          <div className="table-wrap" style={{ marginTop: 12 }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Zone</th>
                  <th>Recs</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {zones.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="faint">
                      No zones yet
                    </td>
                  </tr>
                ) : (
                  zones.map((z) => (
                    <tr
                      key={z.id}
                      className={selectedId === z.id ? "row-active" : ""}
                      style={{ cursor: "pointer" }}
                      onClick={() => setSelectedId(z.id)}
                    >
                      <td>
                        <div className="mono">{z.name}</div>
                        <div className="faint" style={{ fontSize: 12 }}>
                          {z.provider} · {z.syncStatus}
                        </div>
                      </td>
                      <td className="mono faint">{z.recordCount ?? "—"}</td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            void deleteZone(z.id, z.name);
                          }}
                        >
                          Del
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="fm-main">
          {selectedZone ? (
            <>
              <div className="card full" style={{ marginBottom: 16 }}>
                <div className="card-label">Zone · {selectedZone.name}</div>
                <p className="card-meta">
                  SOA serial{" "}
                  <span className="mono">{selectedZone.soaSerial}</span>
                  {" · "}
                  TTL {selectedZone.defaultTtl}s
                  {" · "}
                  NS <span className="mono">{selectedZone.soaPrimaryNs}</span>
                  {" · "}
                  backend <span className="mono">{selectedZone.backend}</span>
                </p>
                {selectedZone.externalNote ? (
                  <p className="card-meta faint">{selectedZone.externalNote}</p>
                ) : null}
                <div className="topbar-actions" style={{ marginTop: 8 }}>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => void exportZone()}
                  >
                    Export zone file
                  </button>
                  <select
                    className="inline-select"
                    value={selectedZone.provider}
                    onChange={(e) =>
                      void setZoneProvider(
                        e.target.value as "local" | "external" | "cloudflare"
                      )
                    }
                  >
                    <option value="local">local</option>
                    <option value="external">external</option>
                    <option value="cloudflare">cloudflare</option>
                  </select>
                </div>
              </div>

              {localEditable ? (
                <>
                  <div className="panel-form" style={{ marginBottom: 16 }}>
                    <div className="panel-form-title">Templates</div>
                    <div className="topbar-actions" style={{ flexWrap: "wrap" }}>
                      {templates.map((t) => (
                        <button
                          key={t.id}
                          type="button"
                          className="btn btn-ghost btn-sm"
                          title={t.description}
                          onClick={() => void applyTemplate(t.id)}
                        >
                          {t.name}
                        </button>
                      ))}
                    </div>
                  </div>

                  <form className="panel-form" onSubmit={saveRecord}>
                    <div className="panel-form-title">
                      {editingId ? "Edit record" : "Add record"}
                    </div>
                    <div className="form-grid">
                      <div className="field">
                        <label>Name</label>
                        <input
                          value={recName}
                          onChange={(e) => setRecName(e.target.value)}
                          placeholder="@ or www"
                          required
                        />
                      </div>
                      <div className="field">
                        <label>Type</label>
                        <select
                          value={recType}
                          onChange={(e) => setRecType(e.target.value)}
                        >
                          {RECORD_TYPES.map((t) => (
                            <option key={t} value={t}>
                              {t}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="field">
                        <label>Content</label>
                        <input
                          value={recContent}
                          onChange={(e) => setRecContent(e.target.value)}
                          placeholder={
                            recType === "A"
                              ? "1.2.3.4"
                              : recType === "MX"
                                ? "mail.example.com"
                                : recType === "TXT"
                                  ? "v=spf1 ..."
                                  : "value"
                          }
                          required
                        />
                      </div>
                      <div className="field">
                        <label>TTL</label>
                        <input
                          type="number"
                          value={recTtl}
                          onChange={(e) => setRecTtl(Number(e.target.value))}
                          min={60}
                        />
                      </div>
                      {recType === "MX" || recType === "SRV" ? (
                        <div className="field">
                          <label>Priority</label>
                          <input
                            type="number"
                            value={recPriority}
                            onChange={(e) =>
                              setRecPriority(Number(e.target.value))
                            }
                            min={0}
                          />
                        </div>
                      ) : null}
                    </div>
                    <div className="topbar-actions">
                      <button className="btn btn-primary btn-inline" type="submit">
                        {editingId ? "Update" : "Add record"}
                      </button>
                      {editingId ? (
                        <button
                          type="button"
                          className="btn btn-ghost"
                          onClick={() => {
                            setEditingId(null);
                            setRecContent("");
                            setRecName("@");
                          }}
                        >
                          Cancel
                        </button>
                      ) : null}
                    </div>
                  </form>
                </>
              ) : (
                <div className="card full" style={{ marginBottom: 16 }}>
                  <p className="card-meta">
                    This zone is in <strong>{selectedZone.provider}</strong>{" "}
                    mode — records are managed outside RealPanel. Switch to{" "}
                    <strong>local</strong> to edit here.
                  </p>
                </div>
              )}

              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Type</th>
                      <th>Content</th>
                      <th>TTL</th>
                      <th>Prio</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {records.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="faint">
                          No records
                        </td>
                      </tr>
                    ) : (
                      records.map((r) => (
                        <tr key={r.id} className={r.disabled ? "faint" : ""}>
                          <td className="mono">{r.name}</td>
                          <td>
                            <span className="badge role-admin">{r.type}</span>
                          </td>
                          <td className="mono" style={{ maxWidth: 280, wordBreak: "break-all" }}>
                            {r.content}
                          </td>
                          <td className="mono faint">{r.ttl}</td>
                          <td className="mono faint">
                            {r.priority != null ? r.priority : "—"}
                          </td>
                          <td>
                            {localEditable ? (
                              <div className="topbar-actions">
                                <button
                                  type="button"
                                  className="btn btn-ghost btn-sm"
                                  onClick={() => startEdit(r)}
                                >
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-ghost btn-sm"
                                  onClick={() => void deleteRecord(r.id)}
                                >
                                  Del
                                </button>
                              </div>
                            ) : null}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="card full">
              <p className="card-meta faint">
                Select a zone or create one. Sites auto-create local zones on
                domain add.
              </p>
            </div>
          )}

          <form
            className="panel-form"
            style={{ marginTop: 24 }}
            onSubmit={runLookup}
          >
            <div className="panel-form-title">DNS lookup (dig)</div>
            <div className="form-grid">
              <div className="field">
                <label>Name</label>
                <input
                  value={lookupName}
                  onChange={(e) => setLookupName(e.target.value)}
                  placeholder="example.com"
                  required
                />
              </div>
              <div className="field">
                <label>Type</label>
                <select
                  value={lookupType}
                  onChange={(e) => setLookupType(e.target.value)}
                >
                  {RECORD_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                  <option value="ANY">ANY</option>
                </select>
              </div>
              <div className="field">
                <label>Server (optional)</label>
                <input
                  value={lookupServer}
                  onChange={(e) => setLookupServer(e.target.value)}
                  placeholder="8.8.8.8"
                />
              </div>
            </div>
            <button className="btn btn-primary btn-inline" type="submit">
              Lookup
            </button>
          </form>

          {lookupResult ? (
            <div className="card full" style={{ marginTop: 12 }}>
              <div className="card-label">
                {lookupResult.name} {lookupResult.type} via{" "}
                {lookupResult.server}
              </div>
              {lookupResult.answers.length === 0 ? (
                <p className="faint">No answers</p>
              ) : (
                <pre className="mono" style={{ fontSize: 12, whiteSpace: "pre-wrap" }}>
                  {lookupResult.answers
                    .map(
                      (a) =>
                        `${a.name}\t${a.ttl}\t${a.type}\t${a.data}`
                    )
                    .join("\n")}
                </pre>
              )}
              {lookupResult.raw ? (
                <pre
                  className="mono faint"
                  style={{ fontSize: 11, marginTop: 8, whiteSpace: "pre-wrap" }}
                >
                  {lookupResult.raw}
                </pre>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
}
